import { useMemo } from 'react';
import { UniqueIdentifier } from '@dnd-kit/core';
import { IntlShape, useIntl } from 'react-intl';
import { useHistory } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import {
  isSchemaSimpleMultiValue,
  isSchemaTableMultiValue,
  SchemaItem,
} from '@rossum/api-client/schemas';
import { getSchemaFieldIcon } from '@rossum/components/schema-field-icon/SchemaFieldIcon';
import {
  Tile,
  TileContextMenuItem,
  TileQuickAction,
} from '@rossum/components/tile';
import { IconCirclePlus } from '@rossum/ui/icons/tabler';
import { capitalize } from '@rossum/ui/material';
import { resolveIconType } from '../../../formulas/FormulaPreview/hooks/utils';
import { buildFieldDetailPath } from '../helpers';
import { SchemaFieldContextAction } from '../types';
import { getQuickActionIcon, QuickActionField } from './form/QuickActionIcon';

type SchemaFieldQuickAction = {
  key: QuickActionField;
  value: boolean;
  disabled: boolean;
  onClick: () => void;
};

type SchemaFieldTileProps = {
  field: SchemaItem;
  parentId?: UniqueIdentifier;
  isFaded?: boolean;
  quickActions: SchemaFieldQuickAction[];
  contextActions?: SchemaFieldContextAction[];
};

const getActionTooltip = (
  intl: IntlShape,
  actionKey: QuickActionField,
  value: boolean
) => {
  const action = intl
    .formatMessage({
      id: `features.queueSettings.fields.quickActions.${actionKey}`,
    })
    .toLowerCase();

  return intl.formatMessage(
    {
      id: `features.queueSettings.fields.quickActions.quick`,
    },
    {
      action,
      newValue: intl
        .formatMessage({
          id: `features.queueSettings.fields.quickActions.${
            value ? 'off' : 'on'
          }`,
        })
        .toLowerCase(),
    }
  );
};

export const SchemaFieldTile = ({
  field,
  parentId,
  isFaded,
  quickActions,
  contextActions,
}: SchemaFieldTileProps) => {
  const history = useHistory();
  const intl = useIntl();

  const type = resolveIconType(field);

  const pathToDetail = useMemo(() => {
    const fieldId = isSchemaSimpleMultiValue(field)
      ? field.children.id
      : field.id;

    return buildFieldDetailPath(
      history.location.pathname,
      fieldId,
      parentId ? String(parentId) : undefined
    );
  }, [field, history.location.pathname, parentId]);

  const description = isSchemaSimpleMultiValue(field)
    ? field.children.id
    : field.id;

  const iconProps = type
    ? {
        content: getSchemaFieldIcon(type, 'default'),
        tooltip: intl.formatMessage(
          {
            id: 'features.queueSettings.fields.fieldTypeTooltip',
          },
          { fieldType: capitalize(type) }
        ),
      }
    : undefined;

  // Convert quick actions to TileQuickAction format
  const tileQuickActions: TileQuickAction[] = quickActions.map(
    ({ key, value, disabled, onClick }) => ({
      key,
      iconProps: {
        Icon: getQuickActionIcon(key, value),
        state: value,
        disabled,
      },
      tooltip: disabled ? '' : getActionTooltip(intl, key, value),
      disabled,
      onClick,
      'data-cy': `quick-action-${key}-button`,
    })
  );

  // Build context menu items
  const contextMenuItems = useMemo((): TileContextMenuItem[] | undefined => {
    if (!contextActions) return undefined;

    const baseItems: TileContextMenuItem[] = contextActions.map(action => ({
      key: action.key,
      label: action.label,
      Icon: action.icon,
      onClick: action.onClick,
      to: action.to,
      'data-cy': `context-action-${action.key}-button`,
    }));

    // For table multivalue fields, add "Add column" at the beginning
    if (isSchemaTableMultiValue(field)) {
      const temporaryColumnId = uuidv4();
      const addColumnItem: TileContextMenuItem = {
        key: 'add-column',
        label: intl.formatMessage({
          id: 'features.queueSettings.fields.contextActions.addColumn',
        }),
        Icon: IconCirclePlus,
        to: {
          pathname: `${pathToDetail}/${temporaryColumnId}`,
          state: { backLink: history.location.pathname },
        },
        'data-cy': 'context-action-add-column-button',
      };

      return [addColumnItem, ...baseItems];
    }

    return baseItems;
  }, [contextActions, field, intl, history, pathToDetail]);

  // Build nested section for table columns (edge case)
  const contextMenuNestedSection = useMemo(() => {
    if (!isSchemaTableMultiValue(field)) return undefined;

    const columnItems: TileContextMenuItem[] = field.children.children.map(
      column => {
        const columnIconType = resolveIconType(column);
        return {
          key: column.id,
          label: column.label,
          Icon: columnIconType
            ? (getSchemaFieldIcon(columnIconType, 'default') ?? undefined)
            : undefined,
          to: {
            pathname: `${pathToDetail}/${column.id}`,
            state: { backLink: history.location.pathname },
          },
          'data-cy': 'context-action-child-detail-button',
        };
      }
    );

    return {
      header: intl.formatMessage({
        id: 'features.queueSettings.fields.contextActions.columns',
      }),
      items: columnItems,
    };
  }, [field, intl, history, pathToDetail]);

  return (
    <Tile
      iconProps={iconProps}
      title={field.label}
      description={description}
      to={pathToDetail}
      disabled={isFaded}
      quickActions={tileQuickActions}
      contextMenuItems={contextMenuItems}
      contextMenuNestedSection={contextMenuNestedSection}
      data-cy="field-tile"
    />
  );
};
